import React, { useEffect, useState } from "react";
import classes from './RequestForm.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import CreateRequisitionModal from "./CreateRequisitionModal";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from "sweetalert2";
import MaterialIssuedModal from "./MaterialIssuedModal";

const RequestForm = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Modal states
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);

    const [viewData, setViewData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isActive, setIsActive] = useState(true);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const [requisitions, setRequisitions] = useState([]);

    useEffect(() => {
        fetchRequisitions();
    }, []);

    const fetchRequisitions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");

            const res = await axios.get(
                `${BASE_URL}/v1/material_requisition`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (res.data?.status && Array.isArray(res.data.data)) {
                setRequisitions(res.data.data);
            } else {
                setRequisitions([]);
            }
        } catch (error) {
            console.error("Error fetching requisitions:", error);
            Swal.fire("Error", "Failed to load requisitions", "error");
        } finally {
            setLoading(false);
        }
    };

    /* =========================
       DELETE REQUISITION
    ========================== */
    const handleDeleteRequisition = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This will delete the requisition permanently",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                await axios.delete(
                    `${BASE_URL}/v1/material_requisition/${id}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                Swal.fire("Deleted!", "Requisition has been deleted.", "success");
                fetchRequisitions(); // Refresh list
            } catch (error) {
                console.error("Error deleting requisition:", error);
                Swal.fire("Error", "Failed to delete requisition", "error");
            }
        }
    };

    /* =========================
       FIXED STATUS CALCULATION FUNCTION
    ========================== */
    const getStatusInfo = (req) => {
        const materials = req.materials || [];

        // Count materials that have been issued (quantity_issued > 0)
        const issuedCount = materials.filter(m => {
            const qtyIssued = parseFloat(m.quantity_issued) || 0;
            return qtyIssued > 0;
        }).length;

        const totalCount = materials.length;

        let status = "Pending";
        let statusColor = "#ffc107"; // Yellow

        if (totalCount === 0) {
            status = "No Materials";
            statusColor = "#6c757d"; // Gray
        } else if (issuedCount === totalCount) {
            status = "Fully Issued";
            statusColor = "#28a745"; // Green
        } else if (issuedCount > 0) {
            status = "Partially Issued";
            statusColor = "#007bff"; // Blue
        } else {
            status = "Pending";
            statusColor = "#ffc107"; // Yellow
        }

        return { status, statusColor, issuedCount, totalCount };
    };

    /* =========================
       FILTER REQUISITIONS
    ========================== */
    const getFilteredRequisitions = () => {
        let filtered = requisitions;

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(req =>
                req.production_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                req.requisition_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                req.created_by?.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter
        if (statusFilter !== "all") {
            filtered = filtered.filter(req => {
                const { status } = getStatusInfo(req);
                return status === statusFilter;
            });
        }

        return filtered;
    };


    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    const MetricSkeleton = () => {
        return (
            <div className={classes.allkads}>
                {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className={classes.skeletonKad}>
                        <div className={classes.skeletonKadInner}>
                            <div className={classes.skeletonKadTitle}></div>
                            <div className={classes.skeletonKadValue}></div>
                            <div className={classes.skeletonKadSubtitle}></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    const filteredRequisitions = getFilteredRequisitions();

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                <div className={classes.actions}>
                    <button
                        className={classes.btns}
                        onClick={() => navigate('/transactions/sales-invoice/sale')}
                    >
                        Sales Invoice
                    </button>
                    <button
                        className={classes.btns}
                        onClick={() => navigate('/Inventory/PurchaseOrder')}
                    >
                        Purchase Order
                    </button>
                    <button
                        className={`${classes.btns} ${isActive ? classes.active : ""}`}
                        onClick={() => setIsActive(true)}
                    >
                        Stock Delivery
                    </button>
                    <button
                        onClick={() => navigate('/settings/product-services')}
                        className={`${classes.btns} ${classes.greens}`}
                    >
                        Product & Services
                    </button>
                </div>

                <header className={classes.Header}>
                    <h2 className={classes.Title}>Raw Material Requisition Form</h2>
                </header>

                {/* Metrics Section */}
                <div className={classes.entrjj}>

                    <div className={classes.allkads}>
                        <div className={classes.thrdkad}>
                            <div className={classes.kad}>
                                <p className={classes.kdptg}>Total Requisitions</p>
                                <p className={classes.kdptg1}>
                                    {requisitions.length}
                                </p>
                                <div className={classes.rvnue}>
                                    <p className={classes.skaltxt}>Total records</p>
                                </div>
                            </div>
                        </div>
                        <div className={classes.frtkad}>
                            <div className={classes.kad}>
                                <p className={classes.kdptg}>Pending</p>
                                <p className={classes.kdptg2}>
                                    {requisitions.filter(req => {
                                        const { status } = getStatusInfo(req);
                                        return status === "Pending";
                                    }).length}
                                </p>
                                <div className={classes.rvnue}>
                                    <p className={classes.skaltxt}>Not yet issued</p>
                                </div>
                            </div>
                        </div>
                        <div className={classes.fiftkad}>
                            <div className={classes.kad}>
                                <p className={classes.kdptg}>Issued</p>
                                <p className={classes.kdptg3}>
                                    {requisitions.filter(req => {
                                        const { status } = getStatusInfo(req);
                                        return status === "Fully Issued";
                                    }).length}
                                </p>
                                <div className={classes.rvnue}>
                                    <p className={classes.skaltxt}>Materials issued</p>
                                </div>
                            </div>
                        </div>
                        <div className={classes.frstkad}>
                            <div className={classes.kad}>
                                <p className={classes.kdptg}>This Month</p>
                                <p className={classes.kdptg4}>
                                    {requisitions.filter(req => {
                                        const reqDate = new Date(req.created_at || req.requisition_date);
                                        const now = new Date();
                                        return reqDate.getMonth() === now.getMonth() &&
                                            reqDate.getFullYear() === now.getFullYear();
                                    }).length}
                                </p>
                                <div className={classes.rvnue}>
                                    <p className={classes.skaltxt}>Monthly requisitions</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div className={classes.headerActions}>
                    <div className={classes.actionsRight}>
                        <div className={classes.inputWrapper}>
                            <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                            <input
                                type="text"
                                className={classes.searchBar}
                                placeholder="Search requisitions"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                        <div className={classes.selectWrapper}>
                            <select
                                className={classes.dropdown}
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                disabled={loading}
                            >
                                <option value="all">All Status</option>
                                <option value="Pending">Pending</option>
                                <option value="Partially Issued">Partially Issued</option>
                                <option value="Fully Issued">Fully Issued</option>
                                <option value="No Materials">No Materials</option>
                            </select>
                            <span className={classes.dropdownArrow}>▼</span>
                        </div>
                        <div className={classes.inputWrapper}>
                            <input
                                type="text"
                                className={classes.searchBars}
                                placeholder="Filter..."
                                disabled={loading}
                            />
                            <img src={filter} alt="Filter" className={classes.inputIcon} />
                        </div>
                    </div>

                    <div className={classes.buttonGroup}>
                        <button
                            className={classes.btnss}
                            onClick={() => setIsCreateModalOpen(true)}
                            disabled={loading}
                        >
                            + New Requisition Form
                        </button>

                        <button
                            className={classes.btnss}
                            onClick={() => setIsIssueModalOpen(true)}
                            style={{ backgroundColor: "#28a745" }}
                            disabled={loading}
                        >
                            + Materials to be Issued
                        </button>
                    </div>
                </div>

                <table className={classes.invoiceTable}>
                    <thead>
                        <tr>
                            <th>S/N</th>
                            <th>Production Order No</th>
                            <th>Request No</th>
                            <th>Description</th>
                            <th>Requested By</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <TableSkeleton />
                        ) : filteredRequisitions.length > 0 ? (
                            filteredRequisitions.map((req, index) => {
                                const { status, statusColor } = getStatusInfo(req);

                                return (
                                    <tr key={req.id}>
                                        <td>{index + 1}</td>
                                        <td>{req.production_number || "N/A"}</td>
                                        <td>{req.requisition_number || "N/A"}</td>
                                        <td>Production materials requisition</td>
                                        <td>{req.created_by?.name || "System Admin"}</td>
                                        <td>
                                            <span style={{
                                                padding: "4px 8px",
                                                borderRadius: "4px",
                                                backgroundColor: statusColor + "20",
                                                color: statusColor,
                                                fontSize: "12px",
                                                fontWeight: "500"
                                            }}>
                                                {status}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{
                                                display: "flex",
                                                gap: "8px",
                                                justifyContent: "center",
                                            }}>
                                                <Eye
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#007bff" }}
                                                    onClick={() => {
                                                        setViewData(req);
                                                        setIsViewModalOpen(true);
                                                    }}
                                                    title="View Details"
                                                />
                                                <Pencil
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#28a745" }}
                                                    onClick={() => {
                                                        // Add edit functionality here
                                                        Swal.fire("Info", "Edit functionality to be implemented", "info");
                                                    }}
                                                    title="Edit"
                                                />
                                                <Trash2
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#dc3545" }}
                                                    onClick={() => handleDeleteRequisition(req.id)}
                                                    title="Delete"
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ textAlign: "center" }}>
                                    {searchTerm || statusFilter !== "all"
                                        ? "No requisitions match your search"
                                        : "No requisitions found"}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* View Modal */}
            {isViewModalOpen && viewData && (
                <div className={classes.modalOverlay}>
                    <div className={classes.viewModal}>
                        <div className={classes.modalHeader}>
                            <h3>Requisition Details</h3>
                            <X
                                size={18}
                                className={classes.closeIcon}
                                onClick={() => {
                                    setIsViewModalOpen(false);
                                    setViewData(null);
                                }}
                            />
                        </div>

                        <div className={classes.modalBody}>
                            <div className={classes.viewRow}>
                                <span>Production Order No</span>
                                <p>{viewData.production_number || "N/A"}</p>
                            </div>

                            <div className={classes.viewRow}>
                                <span>Request No</span>
                                <p>{viewData.requisition_number || "N/A"}</p>
                            </div>

                            <div className={classes.viewRow}>
                                <span>Requisition Date</span>
                                <p>{viewData.requisition_date || viewData.created_at || "N/A"}</p>
                            </div>

                            <div className={classes.viewRow}>
                                <span>Requested By</span>
                                <p>{viewData.created_by?.name || "System Admin"}</p>
                            </div>

                            <div className={classes.viewRow}>
                                <span>Status</span>
                                <p>
                                    <span style={{
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        backgroundColor: getStatusInfo(viewData).statusColor + "20",
                                        color: getStatusInfo(viewData).statusColor,
                                        fontSize: "12px",
                                        fontWeight: "500"
                                    }}>
                                        {getStatusInfo(viewData).status}
                                    </span>
                                </p>
                            </div>

                            <h4 className={classes.tableTitle}>Materials Requested</h4>

                            <table className={classes.materialTable}>
                                <thead>
                                    <tr>
                                        <th>Material</th>
                                        <th>Quantity Required</th>
                                        <th>Quantity Issued</th>
                                        <th>Date Issued</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewData.materials && viewData.materials.length > 0 ? (
                                        viewData.materials.map((item, index) => {
                                            const qtyIssued = parseFloat(item.quantity_issued) || 0;
                                            const qtyRequired = parseFloat(item.quantity_required) || 0;
                                            const isIssued = qtyIssued > 0;
                                            const isFullyIssued = qtyIssued === qtyRequired && qtyRequired > 0;

                                            return (
                                                <tr key={index}>
                                                    <td>{item.item?.name || item.material || "N/A"}</td>
                                                    <td>{qtyRequired}</td>
                                                    <td>
                                                        <span style={{
                                                            color: isIssued ? "#28a745" : "#dc3545",
                                                            fontWeight: isIssued ? "600" : "400"
                                                        }}>
                                                            {isIssued ? qtyIssued : "Not Issued"}
                                                        </span>
                                                    </td>
                                                    <td>{item.issue_date || "N/A"}</td>
                                                    <td>
                                                        <span style={{
                                                            padding: "2px 6px",
                                                            borderRadius: "3px",
                                                            backgroundColor: isFullyIssued ? "#d4edda" : isIssued ? "#cce5ff" : "#f8d7da",
                                                            color: isFullyIssued ? "#155724" : isIssued ? "#004085" : "#721c24",
                                                            fontSize: "11px",
                                                        }}>
                                                            {isFullyIssued ? "Fully Issued" : isIssued ? "Partially Issued" : "Pending"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="5" style={{ textAlign: "center" }}>
                                                No materials found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={classes.modalFooter}>
                            <button
                                className={classes.btnss}
                                onClick={() => {
                                    setIsViewModalOpen(false);
                                    setViewData(null);
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CreateRequisitionModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                refreshList={fetchRequisitions}
            />

            {/* Material Issued Modal */}
            <MaterialIssuedModal
                isOpen={isIssueModalOpen}
                onClose={() => setIsIssueModalOpen(false)}
                refreshList={fetchRequisitions}
            />

            <Footer />
        </div>
    );
};

export default RequestForm;